import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn, NamedLink } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { SERVICE_INFO_DATA } from './serviceInfoData';
import css from './ServiceInfoPage.module.css';
import defaultHeroImage from '../../assets/hero-landscape.jpg';
import heroTreeService from '../../assets/hero-tree-service.jpg';
import heroBrushClearing from '../../assets/hero-brush-clearing.jpg';
import barkTexture from '../../assets/bark-texture.jpg';
import brushTexture from '../../assets/brush-texture.jpg';
import chipsTexture from '../../assets/chips-texture.jpg';
import gravelTexture from '../../assets/gravel-texture.jpg';

import mulchingTexture from '../../assets/mulching-texture.jpg';
import grazingTexture from '../../assets/grazing-texture.jpg';
import metalRoofTexture from '../../assets/metal-roof-texture.jpg';
import stumpGrinderTexture from '../../assets/stump-grinder-texture.jpg';
import inspectorTexture from '../../assets/inspector-texture.jpg';
import heroChipping from '../../assets/hero-chipping.jpg';
import heroDefensibleSpace from '../../assets/hero-defensible-space.jpg';

import heroFireResistantLandscaping from '../../assets/hero-fire-resistant-landscaping.jpg';
import heroGrazing from '../../assets/hero-grazing.jpg';
import heroHomeHardening from '../../assets/hero-home-hardening.jpg';
import heroHeavyMachinery from '../../assets/hero-heavy-machinery.jpg';
import heroConsultation from '../../assets/hero-consultation.jpg';

// Category-specific hero images (falls back to default landscape)
const HERO_IMAGES = {
  tree_service: heroTreeService,
  brush_clearing: heroBrushClearing,
  chipping_debris: heroChipping,
  dsc: heroDefensibleSpace,
  frl: heroFireResistantLandscaping,
  grazing: heroGrazing,
  hh: heroHomeHardening,
  heavy_machinery: heroHeavyMachinery,
  consultation: heroConsultation,
};

// Category-specific hero image positioning (CSS background-position)
const HERO_POSITIONS = {
  brush_clearing: 'center 60%',
  heavy_machinery: 'center 60%',
  consultation: '85% center',
};

// Category-specific bottom CTA background positioning
const BOTTOM_CTA_POSITIONS = {
  hh: 'center 70%',
};

// Category-specific bottom CTA background images (optional)
const BOTTOM_CTA_IMAGES = {
  tree_service: barkTexture,
  brush_clearing: brushTexture,
  chipping_debris: chipsTexture,
  dsc: gravelTexture,
  frl: mulchingTexture,
  grazing: grazingTexture,
  hh: metalRoofTexture,
  heavy_machinery: stumpGrinderTexture,
  consultation: inspectorTexture,
};

// Sidebar quick nav items (same for all categories)
const NAV_ITEMS = [
  { id: 'services', label: 'Services Commonly Offered' },
  { id: 'when-to-call', label: 'When to Call a Professional' },
  { id: 'request-tips', label: 'Request Tips' },
  { id: 'faq', label: 'FAQ' },
];

// Single FAQ item component with expand/collapse
const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div className={classNames(css.faqItem, { [css.faqItemOpen]: isOpen })}>
      <button
        className={css.faqQuestion}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span>{question}</span>
        <span className={css.faqIcon}>{isOpen ? '\u2212' : '+'}</span>
      </button>
      {isOpen && (
        <div className={css.faqAnswer}>
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

export const ServiceInfoPageComponent = props => {
  const { scrollingDisabled, category } = props;
  const intl = useIntl();

  const [openItems, setOpenItems] = useState([]);

  const categoryData = SERVICE_INFO_DATA[category];

  // If category not found in data, render a basic fallback
  if (!categoryData) {
    return (
      <Page title="Service Not Found" scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div style={{ padding: '80px 24px', textAlign: 'center' }}>
            <h1>Service Not Found</h1>
            <p>The service page you requested could not be found.</p>
            <NamedLink name="LandingPage">Return Home</NamedLink>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  const handleToggle = (itemKey) => {
    setOpenItems(prev =>
      prev.includes(itemKey)
        ? prev.filter(key => key !== itemKey)
        : [...prev, itemKey]
    );
  };

  const [colorFrom, colorTo] = categoryData.colors;
  const title = `${categoryData.title} | CA Fire Defense`;

  return (
    <Page title={title} description={categoryData.subtitle} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div
          className={css.root}
          style={{ '--accentFrom': colorFrom, '--accentTo': colorTo }}
        >
          {/* Hero Section */}
          <div
            className={css.hero}
            style={{
              backgroundImage: `url(${HERO_IMAGES[category] || defaultHeroImage})`,
              ...(HERO_POSITIONS[category] ? { backgroundPosition: HERO_POSITIONS[category] } : {}),
            }}
          >
            <div className={css.heroOverlay} />
            <div className={css.heroContent}>
              {(category === 'dsc' || category === 'hh') && (
                <span className={css.fairPlanBadge}>FAIR Plan Eligible</span>
              )}
              <h1 className={css.heroTitle}>{categoryData.title}</h1>
              <p className={css.heroSubtitle}>{categoryData.subtitle}</p>
              <NamedLink
                name="RequestIntakePage"
                params={{ category }}
                className={css.heroCta}
              >
                Go to Request Form
              </NamedLink>
            </div>
          </div>

          {/* Directional Bar */}
          <div className={css.trustBar}>
            <span className={css.trustBarText}>
              Ready to go? <span className={css.trustBarHighlight}>Get started above.</span> Need more information, tips or guides? Check out below.
            </span>
          </div>

          {/* Resource Link Banner */}
          {categoryData.resourceLink && (
            <div className={css.resourceBanner}>
              <span className={css.resourceText}>Not sure where to start? Check out this guide</span>
              <a
                href={categoryData.resourceLink.url}
                target="_blank"
                rel="noopener noreferrer"
                className={css.resourceButton}
              >
                <span className={css.resourceButtonLabel}>CAL FIRE</span>&nbsp;Home Hardening Guide &rarr;
              </a>
            </div>
          )}

          {/* Main Content */}
          <div className={css.content}>
            {/* Sidebar */}
            <aside className={css.sidebar}>
              <nav className={css.quickNav}>
                <h3 className={css.quickNavTitle}>On This Page</h3>
                <ul className={css.quickNavList}>
                  {NAV_ITEMS.map(item => (
                    <li key={item.id}>
                      <a href={`#${item.id}`} className={css.quickNavLink}>
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className={css.sidebarCta}>
                <h3 className={css.sidebarCtaTitle}>Ready to get started?</h3>
                <p className={css.sidebarCtaText}>
                  Describe your project and receive free quotes from local professionals.
                </p>
                <NamedLink
                  name="RequestIntakePage"
                  params={{ category }}
                  className={css.sidebarCtaButton}
                >
                  Go to Request Form
                </NamedLink>
              </div>
            </aside>

            {/* Main Content Area */}
            <main className={css.mainContent}>
              {/* Services Grid */}
              <section id="services" className={css.section}>
                <h2 className={css.sectionTitle}>Services Commonly Offered</h2>
                <p className={css.sectionSubtitle}>
                  From routine maintenance to complex projects, our network of professionals handles it all.
                </p>
                <ul className={css.servicesList}>
                  {categoryData.services.map((service, index) => (
                    <li key={index} className={css.serviceItem}>
                      <span className={css.serviceIcon}>{service.icon}</span>
                      <div>
                        <span className={css.serviceTitle}>{service.title}</span>
                        <span className={css.serviceDescription}>{service.description}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>

              {/* When to Call */}
              <section id="when-to-call" className={css.section}>
                <h2 className={css.sectionTitle}>When to Call a Professional</h2>
                <p className={css.sectionSubtitle}>
                  Not sure if your situation is urgent? Use this guide to decide when to act.
                </p>
                <div className={css.urgencyGrid}>
                  {categoryData.urgency.map((urgency, index) => (
                    <div
                      key={index}
                      className={classNames(css.urgencyCard, css[`urgency${urgency.color}`])}
                    >
                      <h3 className={css.urgencyLevel}>{urgency.level}</h3>
                      <ul className={css.urgencyList}>
                        {urgency.items.map((item, i) => (
                          <li key={i} className={css.urgencyItem}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>

              {/* Request Tips */}
              <section id="request-tips" className={css.section}>
                <h2 className={css.sectionTitle}>Tips for a Great Request</h2>
                <p className={css.sectionSubtitle}>
                  Include these details to get faster, more accurate quotes from contractors.
                </p>
                <div className={css.tipsGrid}>
                  {categoryData.tips.map((tip) => (
                    <div key={tip.number} className={css.tipCard}>
                      <span className={css.tipNumber}>{tip.number}</span>
                      <div className={css.tipContent}>
                        <h3 className={css.tipTitle}>{tip.title}</h3>
                        <p className={css.tipDescription}>{tip.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Mobile-only CTA above FAQ */}
              <div className={css.mobileMidCta}>
                <h3 className={css.mobileMidCtaTitle}>Ready to get started?</h3>
                <p className={css.mobileMidCtaText}>
                  Describe your project and receive free quotes from local professionals.
                </p>
                <NamedLink
                  name="RequestIntakePage"
                  params={{ category }}
                  className={css.mobileMidCtaButton}
                >
                  Go to Request Form
                </NamedLink>
              </div>

              {/* FAQ Accordion */}
              <section id="faq" className={css.section}>
                <h2 className={css.sectionTitle}>Frequently Asked Questions</h2>
                <div className={css.faqList}>
                  {categoryData.faq.map((item, index) => {
                    const itemKey = `faq-${index}`;
                    return (
                      <FAQItem
                        key={itemKey}
                        question={item.q}
                        answer={item.a}
                        isOpen={openItems.includes(itemKey)}
                        onToggle={() => handleToggle(itemKey)}
                      />
                    );
                  })}
                </div>
              </section>
            </main>
          </div>

          {/* Bottom CTA Banner */}
          <div
            className={css.bottomCta}
            style={BOTTOM_CTA_IMAGES[category] ? {
              backgroundImage: `url(${BOTTOM_CTA_IMAGES[category]})`,
              ...(BOTTOM_CTA_POSITIONS[category] ? { backgroundPosition: BOTTOM_CTA_POSITIONS[category] } : {}),
            } : undefined}
          >
            {BOTTOM_CTA_IMAGES[category] && <div className={css.bottomCtaOverlay} />}
            <h2 className={css.bottomCtaTitle}>Ready to Get Started?</h2>
            <p className={css.bottomCtaText}>
              Submit your request in minutes and receive free, no-obligation quotes from local professionals.
            </p>
            <NamedLink
              name="RequestIntakePage"
              params={{ category }}
              className={css.bottomCtaButton}
            >
              Go to Request Form
            </NamedLink>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const ServiceInfoPage = compose(connect(mapStateToProps))(ServiceInfoPageComponent);

export default ServiceInfoPage;
